import { useState, useEffect, useMemo, useCallback } from 'react';
import { adminService } from '../services/adminService';
import { useModal } from '../context/ModalContext';
import { copyTextToClipboard } from '../utils/clipboard.js';

const formatDate = (dateStr) => {
  if (!dateStr) return 'Never';
  const d = new Date(dateStr);
  return d.toLocaleDateString('en-CA');
};

const styles = {
  page: {
    padding: '0',
  },
  card: {
    background: '#fff',
    borderRadius: '8px',
    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.05)',
    padding: '24px',
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '20px',
  },
  sectionTitle: {
    fontSize: '18px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  searchInput: {
    padding: '8px 14px',
    border: '1px solid #d9e0ea',
    borderRadius: '6px',
    fontSize: '14px',
    width: '250px',
    outline: 'none',
  },
  tableWrap: {
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  th: {
    textAlign: 'left',
    padding: '14px 16px',
    borderBottom: '1px solid #d9e0ea',
    color: '#475569',
    fontWeight: 600,
    fontSize: '13px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    whiteSpace: 'nowrap',
  },
  td: {
    padding: '14px 16px',
    borderBottom: '1px solid #eef2f7',
    color: '#1f2937',
    fontSize: '14px',
  },
  badgeActive: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    background: '#d4edda',
    color: '#155724',
  },
  badgeDraft: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    background: '#fff3cd',
    color: '#856404',
  },
  badgeSent: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    background: '#e3f2fd',
    color: '#0288d1',
  },
  badgeExpired: {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 500,
    background: '#f8d7da',
    color: '#721c24',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'nowrap',
  },
  copyBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: 500,
    color: 'var(--company-primary-contrast, #fff)',
    background: 'var(--company-primary-color, #273947)',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  deleteBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#dc3545',
    background: 'transparent',
    border: '1px solid #dc3545',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
  },
  deleteBtnDisabled: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '6px 14px',
    fontSize: '12px',
    fontWeight: 500,
    color: '#999',
    background: 'transparent',
    border: '1px solid #ddd',
    borderRadius: '4px',
    cursor: 'not-allowed',
    whiteSpace: 'nowrap',
  },
  emptyState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#9ca3af',
    fontSize: '15px',
  },
  loadingState: {
    textAlign: 'center',
    padding: '48px 24px',
    color: '#6b7280',
    fontSize: '15px',
  },
};

function getLinkType(link) {
  // Display names only. The underlying `reusable` flag is unchanged, so existing
  // links keep working: reusable = many patients, non-reusable = a single patient.
  if (link.reusable) return 'Reusable Link';
  if (link.patientEmail) return 'One-Off Link (Post-2FA)';
  return 'One-Off Link';
}

function getLinkStatus(link) {
  // Order matters: a submitted link keeps DraftSavedAt set (from the pre-fill or the
  // patient's own draft), and isDraft is only DraftSavedAt.HasValue — so checking
  // draft first would label finished submissions "Draft" forever.
  if (link.status === 'submitted' || link.isSubmitted || link.isUsed) {
    return { label: 'Submitted', style: styles.badgeActive };
  }
  if (link.isExpired) return { label: 'Expired', style: styles.badgeExpired };
  // Only a real saved draft counts. A link generated with pre-filled answers keeps
  // status "ready", so it must not be shown as a draft.
  if (link.status === 'draft_saved') return { label: 'Draft', style: styles.badgeDraft };
  if (link.patientEmail) return { label: 'Sent', style: styles.badgeSent };
  return { label: 'Active', style: styles.badgeActive };
}

export default function ClinicLinksPage() {
  const { showAlert, showConfirm } = useModal();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    loadLinks();
  }, []);

  const loadLinks = async () => {
    setLoading(true);
    try {
      const data = await adminService.getAllLinks();
      const fixedLinks = (Array.isArray(data) ? data : [])
        // In-clinic staff drafts belong under My Submissions (resume via Edit there),
        // so they are hidden here to avoid showing the same record twice. The backend
        // flags them explicitly: a one-off link sent to a patient is NOT an in-clinic
        // draft even after the patient saves and it becomes DraftSaved, so it stays
        // listed. (The old status/email guess wrongly hid one-off links with no
        // recipient email once the patient saved a draft.)
        .filter((link) => !link.isInClinicDraft)
        .map((link) => {
          // A one-off link addressed to a patient carries their email, so the copied
          // URL opens the identity gate with that email pre-filled (resume=1) — the
          // patient still has to pass 2FA, matching the "Resume Form" flow. Reusable
          // links (and any link with no recipient email) have nothing to pre-fill, so
          // they open the normal email-entry gate instead.
          const usePrefilledGate = !link.reusable && Boolean(link.patientEmail);
          link.url = link.token
            ? `${window.location.origin}/fill/${link.token}${usePrefilledGate ? '?resume=1' : ''}`
            : '';
          return link;
        });

      setLinks(fixedLinks);
    } catch (err) {
      console.error('Failed to load links:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredLinks = useMemo(() => {
    if (!search.trim()) return links;
    const q = search.toLowerCase();
    return links.filter(link =>
      (link.formName || '').toLowerCase().includes(q) ||
      (link.doctorEmail || '').toLowerCase().includes(q) ||
      (link.patientName || '').toLowerCase().includes(q) ||
      (link.notes || '').toLowerCase().includes(q)
    );
  }, [links, search]);

  const [copyFeedback, setCopyFeedback] = useState(null);

  const handleCopy = async (url) => {
    // The old fallback called onSuccess() unconditionally, so a failed copy still
    // showed "Link copied to clipboard!" with an empty clipboard.
    const copied = await copyTextToClipboard(url);
    setCopyFeedback(copied
      ? 'Link copied to clipboard!'
      : 'Could not copy — please copy the link manually');
    setTimeout(() => setCopyFeedback(null), 2500);
  };

  const handleDelete = async (token) => {
    const ok = await showConfirm('Are you sure you want to delete this link?', {
      title: 'Delete Link',
      danger: true,
    });
    if (!ok) return;
    try {
      await adminService.deleteLink(token);
      await loadLinks();
    } catch (err) {
      await showAlert(err?.message || 'Failed to delete link', 'Error');
    }
  };

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <div style={styles.sectionHeader}>
          <h2 style={styles.sectionTitle}>Assigned Links</h2>
          <input
            type="text"
            placeholder="Search links..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>


        {loading && <div style={styles.loadingState}>Loading...</div>}

        {!loading && filteredLinks.length === 0 && (
          <div style={styles.emptyState}>No links found.</div>
        )}

        {!loading && filteredLinks.length > 0 && (
          <div style={styles.tableWrap}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Template Name</th>
                  <th style={styles.th}>Form Recipient Email</th>
                  <th style={styles.th}>CC Email</th>
                  <th style={styles.th}>BCC Email</th>
                  <th style={styles.th}>Expiry Date</th>
                  <th style={styles.th}>Link Type</th>
                  <th style={styles.th}>Status</th>
                  <th style={styles.th}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredLinks.map((link) => {
                  const status = getLinkStatus(link);
                  const canDelete = !link.createdByAdmin;
                  return (
                    <tr key={link.token}>
                      <td style={styles.td}>{link.formName || '—'}</td>
                      <td style={styles.td}>{link.doctorEmail || '—'}</td>
                      <td style={styles.td}>{link.ccEmail || '—'}</td>
                    <td style={styles.td}>{link.bccEmail || '—'}</td>
                      <td style={styles.td}>{formatDate(link.expiresAt)}</td>
                      <td style={styles.td}>{getLinkType(link)}</td>
                      <td style={styles.td}>
                        <span style={status.style}>{status.label}</span>
                      </td>
                      <td style={styles.td}>
                        <div style={styles.actions}>
                          <button style={styles.copyBtn} onClick={() => handleCopy(link.url)} disabled={!link.url}>
                            Copy Link
                          </button>
                          <button
                            style={canDelete ? styles.deleteBtn : styles.deleteBtnDisabled}
                            onClick={canDelete ? () => handleDelete(link.token) : undefined}
                            disabled={!canDelete}
                            title={!canDelete ? 'Cannot delete links created by Admin' : 'Delete this link'}
                          >
                            Delete Link
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {copyFeedback && (
        <div style={{
          position: 'fixed',
          bottom: '24px',
          right: '24px',
          background: 'var(--company-primary-color, #273947)',
          color: 'var(--company-primary-contrast, #fff)',
          padding: '12px 20px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: 500,
          boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
          zIndex: 9999,
        }}>
          {copyFeedback}
        </div>
      )}
    </div>
  );
}
